import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Eye, Check, X, Clock, Image, FileSignature, Package, Users, Link2, AlertTriangle, Trash2, CheckSquare, Pencil, Save, Plus, Minimize2, Calendar as CalendarIcon, Filter, ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { calcHoursDecimal as calcHours, formatHours as formatHoursUtil } from '@/lib/timeUtils';
import { AccontoSelector } from '@/components/AccontoSelector';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

// Wrapper that prefers minute-based derivation when both times are available
const formatHours = (decimal, ora_inizio, ora_fine) => formatHoursUtil(decimal, ora_inizio, ora_fine);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RapportiniPage() {
  const [rapportini, setRapportini] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCantiere, setFilterCantiere] = useState('all');
  const [filterWorker, setFilterWorker] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedRapportino, setSelectedRapportino] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [approveAcconto, setApproveAcconto] = useState('');
  const [linkCantiereId, setLinkCantiereId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createMinimized, setCreateMinimized] = useState(false);
  // New cantiere inline creation
  const [newCantiereDialog, setNewCantiereDialog] = useState(false);
  const [newCantiereForm, setNewCantiereForm] = useState({ name: '', address: '', client_name: '' });
  const [creatingCantiere, setCreatingCantiere] = useState(false);
  const [createForm, setCreateForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours_worked: 8,
    ore_straordinarie: 0,
    ora_inizio: '08:00',
    ora_fine: '16:00',
    description: '',
    cantiere_id: '',
    trasferta: false,
    status: 'approvato',
    acconto_riferimento: '',
    workers_list: []
  });

  const fetchRapportini = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rapportini`, { withCredentials: true });
      setRapportini(response.data);
    } catch (error) {
      console.error('Errore caricamento rapportini:', error);
      toast.error('Errore nel caricamento rapportini');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCantieri = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cantieri`, { withCredentials: true });
      setCantieri(response.data);
    } catch (error) {
      console.error('Error fetching cantieri:', error);
    }
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users?role=operaio`, { withCredentials: true });
      setWorkers(response.data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  }, []);

  useEffect(() => {
    fetchRapportini();
    fetchCantieri();
    fetchWorkers();
  }, [fetchRapportini, fetchCantieri, fetchWorkers]);

  const handleStatusUpdate = async (id, status) => {
    if (status === 'approvato' && !approveAcconto) {
      toast.error('Indica il riferimento: Acconto n° oppure Extra');
      return;
    }
    try {
      const payload = { status, admin_notes: adminNotes };
      if (status === 'approvato') payload.acconto_riferimento = approveAcconto;
      await axios.put(`${API}/rapportini/${id}`, payload, { withCredentials: true });
      toast.success(`Rapportino ${status === 'approvato' ? 'approvato' : 'rifiutato'}`);
      setDetailDialogOpen(false);
      fetchRapportini();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const openDetailDialog = (rapportino) => {
    setSelectedRapportino(rapportino);
    setAdminNotes(rapportino.admin_notes || '');
    setApproveAcconto(rapportino.acconto_riferimento || '');
    setEditing(false);
    setEditForm({
      date: rapportino.date || '',
      hours_worked: rapportino.hours_worked || 0,
      ore_straordinarie: rapportino.ore_straordinarie || 0,
      ora_inizio: rapportino.ora_inizio || '',
      ora_fine: rapportino.ora_fine || '',
      description: rapportino.description || '',
      worker_name: rapportino.worker_name || '',
      operai_tecnici: rapportino.operai_tecnici || '',
      num_workers: rapportino.num_workers || 1,
      trasferta: rapportino.trasferta || false,
      cantiere_id: rapportino.cantiere_id || '',
      status: rapportino.status || 'in_attesa',
      admin_notes: rapportino.admin_notes || '',
      commessa: rapportino.commessa || '',
      acconto_riferimento: rapportino.acconto_riferimento || '',
      workers_list: rapportino.workers_list || []
    });
    setDetailDialogOpen(true);
  };

  const addWorkerToList = (workerId) => {
    const w = workers.find(w => w.id === workerId);
    if (!w) return;
    if (editForm.workers_list.some(wl => wl.worker_id === workerId)) return;
    setEditForm({
      ...editForm,
      workers_list: [...editForm.workers_list, { worker_id: w.id, name: w.name }],
      num_workers: editForm.workers_list.length + 1
    });
  };

  const addManualWorker = (name) => {
    if (!name.trim()) return;
    if (editForm.workers_list.some(wl => wl.name.toLowerCase() === name.trim().toLowerCase())) return;
    setEditForm({
      ...editForm,
      workers_list: [...editForm.workers_list, { worker_id: '', name: name.trim() }],
      num_workers: editForm.workers_list.length + 1
    });
  };

  const removeWorkerFromList = (index) => {
    const newList = editForm.workers_list.filter((_, i) => i !== index);
    setEditForm({ ...editForm, workers_list: newList, num_workers: Math.max(1, newList.length) });
  };

  const handleSaveEdit = async () => {
    try {
      // Auto-set worker_name and operai_tecnici from workers_list
      const data = { ...editForm };
      if (data.workers_list.length > 0) {
        data.worker_name = data.workers_list[0].name;
        data.worker_id = data.workers_list[0].worker_id;
        data.operai_tecnici = data.workers_list.length > 1
          ? `Squadra di ${data.workers_list.length} persone`
          : data.workers_list[0].name;
        data.num_workers = data.workers_list.length;
      }
      await axios.put(`${API}/rapportini/${selectedRapportino.id}`, data, { withCredentials: true });
      toast.success('Rapportino aggiornato');
      setDetailDialogOpen(false);
      setEditing(false);
      fetchRapportini();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const openLinkDialog = (rapportino) => {
    setSelectedRapportino(rapportino);
    setLinkCantiereId('');
    setLinkDialogOpen(true);
  };

  const handleLinkCantiere = async () => {
    if (!linkCantiereId) {
      toast.error('Seleziona un cantiere');
      return;
    }
    try {
      await axios.put(`${API}/rapportini/${selectedRapportino.id}/link-cantiere?cantiere_id=${linkCantiereId}`, {}, { withCredentials: true });
      toast.success('Rapportino collegato al cantiere');
      setLinkDialogOpen(false);
      fetchRapportini();
    } catch (error) {
      toast.error('Errore nel collegamento');
    }
  };

  const handleDeleteRapportino = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo rapportino?')) return;
    try {
      await axios.delete(`${API}/rapportini/${id}`, { withCredentials: true });
      toast.success('Rapportino eliminato');
      fetchRapportini();
    } catch { toast.error('Errore'); }
  };

  const handleCreateInlineCantiere = async (e) => {
    e?.preventDefault?.();
    if (!newCantiereForm.name.trim()) { toast.error('Nome cantiere obbligatorio'); return; }
    setCreatingCantiere(true);
    try {
      const res = await axios.post(`${API}/cantieri`, {
        name: newCantiereForm.name,
        address: newCantiereForm.address || '',
        description: '',
        status: 'in_corso',
        start_date: null,
        end_date: null,
        client_name: newCantiereForm.client_name || '',
        cliente_id: null,
        prezzo_preventivato: 0,
      }, { withCredentials: true });
      const created = res.data;
      // Refresh cantieri list and pre-select the new one
      await fetchCantieri();
      setCreateForm(prev => ({ ...prev, cantiere_id: created.id }));
      toast.success(`Cantiere "${created.name}" creato e selezionato`);
      setNewCantiereDialog(false);
      setNewCantiereForm({ name: '', address: '', client_name: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione cantiere');
    } finally {
      setCreatingCantiere(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredRapportini.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredRapportini.map(r => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/rapportini/bulk-delete`, { data: { rapportini_ids: Array.from(selected) }, withCredentials: true });
      toast.success(`${res.data.deleted} rapportini eliminati`);
      setSelected(new Set());
      fetchRapportini();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setDeleting(false); }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/rapportini/delete-all`, { withCredentials: true });
      toast.success(res.data.message);
      setSelected(new Set());
      setDeleteAllConfirm(false);
      fetchRapportini();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setDeleting(false); }
  };

  const handleCreateRapportino = async () => {
    if (!createForm.cantiere_id) { toast.error('Seleziona un cantiere'); return; }
    if (createForm.workers_list.length === 0) { toast.error('Aggiungi almeno un operaio'); return; }
    if (createForm.status === 'approvato' && !createForm.acconto_riferimento) { toast.error('Indica il riferimento: Acconto n° oppure Extra'); return; }
    try {
      const data = {
        ...createForm,
        worker_name: createForm.workers_list[0]?.name || '',
        worker_id: createForm.workers_list[0]?.worker_id || '',
        num_workers: createForm.workers_list.length,
        operai_tecnici: createForm.workers_list.length > 1
          ? `Squadra di ${createForm.workers_list.length} persone`
          : createForm.workers_list[0]?.name || ''
      };
      await axios.put(`${API}/rapportini/admin-create`, data, { withCredentials: true });
      toast.success('Rapportino creato');
      setCreateOpen(false);
      setCreateMinimized(false);
      setCreateForm({ date: new Date().toISOString().split('T')[0], hours_worked: 8, ore_straordinarie: 0, ora_inizio: '08:00', ora_fine: '16:00', description: '', cantiere_id: '', trasferta: false, status: 'approvato', acconto_riferimento: '', workers_list: [] });
      fetchRapportini();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const addCreateWorker = (workerId) => {
    const w = workers.find(w => w.id === workerId);
    if (!w || createForm.workers_list.some(wl => wl.worker_id === workerId)) return;
    setCreateForm({ ...createForm, workers_list: [...createForm.workers_list, { worker_id: w.id, name: w.name }] });
  };

  const removeCreateWorker = (idx) => {
    setCreateForm({ ...createForm, workers_list: createForm.workers_list.filter((_, i) => i !== idx) });
  };

  const getCantiereNome = (cantiereId) => {
    if (!cantiereId) return null;
    const cantiere = cantieri.find(c => c.id === cantiereId);
    if (cantiere) return cantiere.name;
    // Legacy imported rapportini stored the cantiere/customer name directly in
    // `cantiere_id` (instead of a UUID). When the value is not a valid UUID we
    // assume it's the human-readable name and surface it as-is.
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cantiereId);
    if (!isUUID) return cantiereId;
    return null;
  };

  const isUnlinked = (cantiereId) => {
    if (!cantiereId) return false;
    // Only flag as "unlinked" UUID-shaped ids that don't resolve to any cantiere
    // in the lookup. Free-text legacy ids (imported rapportini) are considered
    // linked because the value itself carries the cantiere/customer name.
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cantiereId);
    if (!isUUID) return false;
    return !cantieri.find(c => c.id === cantiereId);
  };

  const statusColors = {
    in_attesa: 'bg-amber-100 text-amber-800 border-amber-200',
    approvato: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    rifiutato: 'bg-red-100 text-red-800 border-red-200'
  };

  const statusLabels = {
    in_attesa: 'In Attesa',
    approvato: 'Approvato',
    rifiutato: 'Rifiutato'
  };

  const filteredRapportini = useMemo(() => {
    return rapportini.filter(r => {
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterCantiere === 'unlinked') {
        if (!isUnlinked(r.cantiere_id)) return false;
      } else if (filterCantiere !== 'all' && r.cantiere_id !== filterCantiere) {
        return false;
      }
      if (filterWorker !== 'all') {
        const inCreator = r.worker_id === filterWorker;
        const inList = (r.workers_list || []).some(w => w.worker_id === filterWorker);
        if (!inCreator && !inList) return false;
      }
      if (filterDateFrom && r.date < filterDateFrom) return false;
      if (filterDateTo && r.date > filterDateTo) return false;
      return true;
    });
    // `isUnlinked` depends on `cantieri`, so we include it explicitly to
    // avoid stale-filter bugs when cantieri load after rapportini.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rapportini, cantieri, filterStatus, filterCantiere, filterWorker, filterDateFrom, filterDateTo]);

  // Group by month (YYYY-MM)
  const groupedByMonth = useMemo(() => {
    const groups = {};
    for (const r of filteredRapportini) {
      const key = (r.date || '').slice(0, 7); // YYYY-MM
      if (!key) continue;
      (groups[key] = groups[key] || []).push(r);
    }
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [filteredRapportini]);

  const formatMonth = (ym) => {
    if (!ym) return '';
    try {
      const [y, m] = ym.split('-');
      const d = new Date(Number(y), Number(m) - 1, 1);
      const txt = format(d, 'MMMM yyyy', { locale: it });
      return txt.charAt(0).toUpperCase() + txt.slice(1);
    } catch { return ym; }
  };

  const toggleMonth = (key) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Auto-expand current month on first load
  useEffect(() => {
    if (groupedByMonth.length > 0 && expandedMonths.size === 0) {
      setExpandedMonths(new Set([groupedByMonth[0][0]]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByMonth.length]);

  const activeFiltersCount = [
    filterStatus !== 'all',
    filterCantiere !== 'all',
    filterWorker !== 'all',
    !!filterDateFrom,
    !!filterDateTo,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterCantiere('all');
    setFilterWorker('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCantiere !== 'all' && filterCantiere !== 'unlinked') params.append('cantiere_id', filterCantiere);
      if (filterWorker !== 'all') params.append('worker_id', filterWorker);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const res = await axios.get(`${API}/rapportini/export?${params.toString()}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapportini_${new Date().toISOString().slice(0, 10)}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format === 'csv' ? 'CSV' : 'Excel'} completato`);
    } catch (error) {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  // Recompute when either rapportini or cantieri change (isUnlinked reads cantieri)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const unlinkedCount = useMemo(() => rapportini.filter(r => isUnlinked(r.cantiere_id)).length, [rapportini, cantieri]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="rapportini-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Rapportini</h1>
          <p className="text-slate-500 mt-1">Gestisci e approva i rapportini degli operai</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting} data-testid="export-rapportini-btn">
                <Download className="h-4 w-4 mr-2" />{exporting ? 'Esportazione...' : 'Esporta'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx-item">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv-item">
                <FileText className="h-4 w-4 mr-2 text-blue-600" />CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {rapportini.length > 0 && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteAllConfirm(true)} data-testid="delete-all-rapportini-btn">
              <Trash2 className="h-4 w-4 mr-2" />Elimina Tutti
            </Button>
          )}
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setCreateOpen(true)} data-testid="create-rapportino-btn">
            <Plus className="h-4 w-4 mr-2" />Nuovo Rapportino
          </Button>
        </div>
      </div>

      {/* Unlinked badge */}
      {unlinkedCount > 0 && (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200" variant="outline" data-testid="unlinked-badge">
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          {unlinkedCount} da collegare
        </Badge>
      )}

      {/* Selection action bar */}
      {selected.size > 0 && (
        <Card className="border-blue-300 bg-blue-50 sticky top-0 z-10">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
              <CheckSquare className="h-4 w-4 mr-1" />{selected.size} selezionati
            </Badge>
            <Button variant="destructive" disabled={deleting} onClick={handleBulkDelete} data-testid="bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" />{deleting ? 'Eliminazione...' : 'Elimina Selezionati'}
            </Button>
            <Button variant="ghost" onClick={() => setSelected(new Set())} className="text-slate-500">Deseleziona</Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setFiltersOpen(o => !o)} className="text-slate-700" data-testid="toggle-filters">
              <Filter className="h-4 w-4 mr-1.5" />Filtri
              {activeFiltersCount > 0 && (
                <Badge className="ml-2 bg-blue-600 text-white">{activeFiltersCount}</Badge>
              )}
              <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </Button>
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters">
                <X className="h-3.5 w-3.5 mr-1" />Reset filtri
              </Button>
            )}
          </div>
          {filtersOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-slate-100">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Stato</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                    <SelectItem value="approvato">Approvati</SelectItem>
                    <SelectItem value="rifiutato">Rifiutati</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Cantiere</Label>
                <Select value={filterCantiere} onValueChange={setFilterCantiere}>
                  <SelectTrigger data-testid="filter-cantiere"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i cantieri</SelectItem>
                    {unlinkedCount > 0 && (
                      <SelectItem value="unlinked">Non collegati ({unlinkedCount})</SelectItem>
                    )}
                    {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Operaio</Label>
                <Select value={filterWorker} onValueChange={setFilterWorker}>
                  <SelectTrigger data-testid="filter-worker"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli operai</SelectItem>
                    {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Da data</Label>
                <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">A data</Label>
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} data-testid="filter-date-to" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table grouped by month */}
      {groupedByMonth.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">Nessun rapportino trovato</CardContent>
        </Card>
      ) : (
        groupedByMonth.map(([monthKey, monthRows]) => {
          const isExpanded = expandedMonths.has(monthKey);
          const totalHours = monthRows.reduce((s, r) => s + (r.hours_worked || 0), 0);
          return (
            <Card key={monthKey} className="border-slate-200">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition border-b border-slate-100"
                onClick={() => toggleMonth(monthKey)}
                data-testid={`month-toggle-${monthKey}`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <CalendarIcon className="h-4 w-4 text-slate-500" />
                  <span className="font-semibold text-slate-900">{formatMonth(monthKey)}</span>
                  <Badge variant="outline" className="ml-1 text-xs">{monthRows.length} rapportini</Badge>
                </div>
                <span className="text-xs text-slate-500">{Math.round(totalHours * 10) / 10}h totali</span>
              </button>
              {isExpanded && (
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 border-slate-200">
                        <TableHead className="w-10">
                          <Checkbox checked={monthRows.length > 0 && monthRows.every(r => selected.has(r.id))} onCheckedChange={(checked) => {
                            setSelected(prev => {
                              const next = new Set(prev);
                              monthRows.forEach(r => { if (checked) next.add(r.id); else next.delete(r.id); });
                              return next;
                            });
                          }} />
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">N.</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Operaio</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cantiere</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Ore</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500">Extra</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthRows.map((rapportino) => {
                        const cantiereName = getCantiereNome(rapportino.cantiere_id);
                        const unlinked = isUnlinked(rapportino.cantiere_id);
                        return (
                          <TableRow key={rapportino.id} className={`border-slate-100 hover:bg-slate-50 ${unlinked ? 'bg-amber-50/50' : ''} ${selected.has(rapportino.id) ? 'bg-blue-50' : ''}`}>
                            <TableCell>
                              <Checkbox checked={selected.has(rapportino.id)} onCheckedChange={() => toggleSelect(rapportino.id)} />
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">
                              #{rapportino.numero || '-'}
                            </TableCell>
                            <TableCell className="text-slate-900">
                              {fmtDate(rapportino.date, 'dd-MM-yyyy')}
                            </TableCell>
                            <TableCell className="font-medium text-slate-900">{rapportino.worker_name}</TableCell>
                            <TableCell>
                              {unlinked ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-amber-700 text-sm">{rapportino.cantiere_id}</span>
                                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-blue-600" onClick={(e) => { e.stopPropagation(); openLinkDialog(rapportino); }} data-testid={`link-cantiere-${rapportino.id}`}>
                                    <Link2 className="h-3.5 w-3.5 mr-0.5" />Collega
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-slate-600">{cantiereName || rapportino.cantiere_id}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-900">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4 text-slate-400" />
                                  {formatHours(rapportino.hours_worked, rapportino.ora_inizio, rapportino.ora_fine)}
                                </div>
                                {rapportino.ora_inizio && rapportino.ora_fine && (
                                  <span className="text-xs text-slate-400">{rapportino.ora_inizio}-{rapportino.ora_fine}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusColors[rapportino.status]} variant="outline">
                                {statusLabels[rapportino.status]}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {rapportino.photo_urls?.length > 0 && <Image className="h-4 w-4 text-slate-400" />}
                                {rapportino.signature_data && <FileSignature className="h-4 w-4 text-slate-400" />}
                                {rapportino.materials_used?.length > 0 && <Package className="h-4 w-4 text-slate-400" />}
                                {rapportino.colleghi?.length > 0 && (
                                  <span className="flex items-center gap-0.5 text-xs text-blue-600">
                                    <Users className="h-3.5 w-3.5" />{rapportino.colleghi.length}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openDetailDialog(rapportino)} data-testid={`view-rapportino-${rapportino.id}`}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRapportino(rapportino.id)} data-testid={`delete-rapportino-${rapportino.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      {/* Detail / Edit Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Rapportino #{selectedRapportino?.numero || '-'}</DialogTitle>
              {!editing && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-rapportino-btn">
                  <Pencil className="h-4 w-4 mr-1" />Modifica
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedRapportino && !editing && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Data</p>
                  <p className="font-medium">{fmtDate(selectedRapportino.date, 'dd MMMM yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Operaio</p>
                  <p className="font-medium">{selectedRapportino.worker_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Cantiere</p>
                  <p className="font-medium">
                    {getCantiereNome(selectedRapportino.cantiere_id) || selectedRapportino.cantiere_id}
                    {isUnlinked(selectedRapportino.cantiere_id) && (
                      <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">Non collegato</Badge>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ore Lavorate</p>
                  <p className="font-medium">{formatHours(selectedRapportino.hours_worked, selectedRapportino.ora_inizio, selectedRapportino.ora_fine)}
                    {selectedRapportino.ora_inizio && selectedRapportino.ora_fine && (
                      <span className="text-slate-400 font-normal ml-1">({selectedRapportino.ora_inizio} - {selectedRapportino.ora_fine})</span>
                    )}
                  </p>
                </div>
                {selectedRapportino.operai_tecnici && (
                  <div>
                    <p className="text-sm text-slate-500">Operai/Tecnici</p>
                    <p className="font-medium">{selectedRapportino.operai_tecnici}</p>
                  </div>
                )}
                {selectedRapportino.commessa && (
                  <div>
                    <p className="text-sm text-slate-500">Commessa</p>
                    <p className="font-medium">#{selectedRapportino.commessa}</p>
                  </div>
                )}
              </div>
              {selectedRapportino.workers_list?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Operai Presenti</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRapportino.workers_list.map((w, idx) => (
                      <span key={w.worker_id || `manual-${w.name}-${idx}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-700">
                        {w.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500 mb-1">Descrizione Lavori</p>
                <p className="p-3 bg-slate-50 rounded-lg border border-slate-200">{selectedRapportino.description || '-'}</p>
              </div>
              {selectedRapportino.colleghi?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Colleghi Presenti</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRapportino.colleghi.map((collega, idx) => (
                      <span key={collega.id || `coll-${collega.name}-${idx}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700">
                        {collega.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedRapportino.signature_data && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Firma Digitale</p>
                  <div className="border border-slate-200 rounded-lg p-2 bg-white">
                    <img src={selectedRapportino.signature_data} alt="Firma" className="max-h-24" />
                  </div>
                </div>
              )}
              {isUnlinked(selectedRapportino.cantiere_id) && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700 mb-2">Cantiere non collegato.</p>
                  <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => { setDetailDialogOpen(false); openLinkDialog(selectedRapportino); }}>
                    <Link2 className="h-4 w-4 mr-1.5" />Collega a un cantiere
                  </Button>
                </div>
              )}
              {selectedRapportino.status === 'in_attesa' && (
                <>
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <AccontoSelector value={approveAcconto} onChange={setApproveAcconto} label="Riferimento acconto (obbligatorio per approvare) *" hint="Assegna il rapportino a un acconto oppure segnalalo come Extra (lavori fuori preventivo)" dataTestId="approve-acconto-select" />
                  </div>
                  <div className="space-y-2">
                    <Label>Note Admin</Label>
                    <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Aggiungi note opzionali..." data-testid="admin-notes-input" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleStatusUpdate(selectedRapportino.id, 'rifiutato')} data-testid="reject-rapportino-btn">
                      <X className="h-4 w-4 mr-2" />Rifiuta
                    </Button>
                    <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => handleStatusUpdate(selectedRapportino.id, 'approvato')} data-testid="approve-rapportino-btn">
                      <Check className="h-4 w-4 mr-2" />Approva
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          {selectedRapportino && editing && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Data</Label>
                  <input type="date" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} data-testid="edit-date" />
                </div>
                <div className="space-y-1">
                  <Label>Cantiere</Label>
                  <Select value={editForm.cantiere_id} onValueChange={v => setEditForm({...editForm, cantiere_id: v})}>
                    <SelectTrigger data-testid="edit-cantiere"><SelectValue placeholder="Scegli cantiere" /></SelectTrigger>
                    <SelectContent>
                      {cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Operai Presenti - UNICO campo */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Operai Presenti</Label>
                <div className="flex flex-wrap gap-2 min-h-[42px] p-2.5 border border-slate-200 rounded-md bg-white">
                  {editForm.workers_list.map((w, idx) => (
                    <span key={w.worker_id || `manual-${w.name}-${idx}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-700">
                      {w.name}
                      <button type="button" className="ml-0.5 text-blue-400 hover:text-red-500" onClick={() => removeWorkerFromList(idx)}><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                  {editForm.workers_list.length === 0 && <span className="text-sm text-slate-400">Nessun operaio — aggiungi dal menu sotto</span>}
                </div>
                <div className="flex gap-2">
                  <Select value="" onValueChange={addWorkerToList}>
                    <SelectTrigger className="w-[220px]" data-testid="add-worker-list"><SelectValue placeholder="Seleziona operaio..." /></SelectTrigger>
                    <SelectContent>
                      {workers.filter(w => !editForm.workers_list.some(wl => wl.worker_id === w.id)).map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <form className="flex gap-1 flex-1" onSubmit={e => { e.preventDefault(); const v = e.target.elements.manualWorker.value; addManualWorker(v); e.target.reset(); }}>
                    <input name="manualWorker" type="text" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="o scrivi nome + Invio" />
                  </form>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Ora Inizio</Label>
                  <input type="time" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={editForm.ora_inizio} onChange={e => { const v = e.target.value; const h = calcHours(v, editForm.ora_fine); setEditForm({...editForm, ora_inizio: v, hours_worked: h, ore_straordinarie: h > 9 ? Math.round((h-9)*100)/100 : 0}); }} />
                </div>
                <div className="space-y-1">
                  <Label>Ora Fine</Label>
                  <input type="time" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={editForm.ora_fine} onChange={e => { const v = e.target.value; const h = calcHours(editForm.ora_inizio, v); setEditForm({...editForm, ora_fine: v, hours_worked: h, ore_straordinarie: h > 9 ? Math.round((h-9)*100)/100 : 0}); }} />
                </div>
                <div className="space-y-1">
                  <Label>Ore Calcolate</Label>
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold">{formatHours(editForm.hours_worked, editForm.ora_inizio, editForm.ora_fine)}</div>
                </div>
                <div className="space-y-1">
                  <Label>Straordinari</Label>
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-amber-600">{formatHours(editForm.ore_straordinarie)}</div>
                </div>
                <div className="space-y-1">
                  <Label>N. Persone</Label>
                  <input type="number" min="1" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={editForm.num_workers} onChange={e => setEditForm({...editForm, num_workers: parseInt(e.target.value) || 1})} />
                </div>
                <div className="space-y-1">
                  <Label>Commessa</Label>
                  <input type="text" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={editForm.commessa} onChange={e => setEditForm({...editForm, commessa: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <Label>Stato</Label>
                  <Select value={editForm.status} onValueChange={v => setEditForm({...editForm, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_attesa">In Attesa</SelectItem>
                      <SelectItem value="approvato">Approvato</SelectItem>
                      <SelectItem value="rifiutato">Rifiutato</SelectItem>
                      <SelectItem value="importato">Importato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 col-span-2">
                  <AccontoSelector value={editForm.acconto_riferimento} onChange={v => setEditForm({...editForm, acconto_riferimento: v})} label="Acconto / Extra" dataTestId="edit-acconto-select" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox checked={editForm.trasferta} onCheckedChange={v => setEditForm({...editForm, trasferta: v})} />
                  <Label>Trasferta</Label>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Descrizione</Label>
                <Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} data-testid="edit-description" />
              </div>
              <div className="space-y-1">
                <Label>Note Admin</Label>
                <Textarea value={editForm.admin_notes} onChange={e => setEditForm({...editForm, admin_notes: e.target.value})} rows={2} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(false)}>Annulla</Button>
                <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={handleSaveEdit} data-testid="save-edit-btn">
                  <Save className="h-4 w-4 mr-2" />Salva Modifiche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Cantiere Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Collega Rapportino a Cantiere</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-500">Cantiere inserito dall'operaio:</p>
              <p className="font-medium text-slate-900">{selectedRapportino?.cantiere_id}</p>
              <p className="text-xs text-slate-400 mt-1">Rapportino di {selectedRapportino?.worker_name} del {selectedRapportino?.date}</p>
            </div>
            <div className="space-y-2">
              <Label>Seleziona il cantiere da collegare</Label>
              <Select value={linkCantiereId} onValueChange={setLinkCantiereId}>
                <SelectTrigger data-testid="link-cantiere-select">
                  <SelectValue placeholder="Seleziona cantiere..." />
                </SelectTrigger>
                <SelectContent>
                  {cantieri.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} - {c.address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Annulla</Button>
              <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleLinkCantiere} disabled={!linkCantiereId} data-testid="confirm-link-btn">
                <Link2 className="h-4 w-4 mr-2" />Collega
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirm */}
      <AlertDialog open={deleteAllConfirm} onOpenChange={setDeleteAllConfirm}>
        <AlertDialogContent data-testid="delete-all-rapportini-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />Elimina tutti i rapportini</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati <strong>{rapportini.length}</strong> rapportini. Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll} className="bg-red-600 hover:bg-red-700 text-white" disabled={deleting} data-testid="confirm-delete-all-rapportini-btn">
              {deleting ? 'Eliminazione...' : 'Elimina Tutti'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Rapportino Dialog */}
      <Dialog open={createOpen && !createMinimized} onOpenChange={(open) => {
        if (!open && !createMinimized) {
          // Block close: ask user
          // No-op: dialog needs to be closed only via the X explicitly
        }
        setCreateOpen(open);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Nuovo Rapportino</DialogTitle>
              <Button variant="ghost" size="sm" onClick={() => setCreateMinimized(true)} className="h-8" data-testid="minimize-create-btn" title="Riduci a icona (i dati vengono mantenuti)">
                <Minimize2 className="h-4 w-4 mr-1" /><span className="text-xs">Riduci</span>
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data</Label>
                <input type="date" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={createForm.date} onChange={e => setCreateForm({...createForm, date: e.target.value})} />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>Cantiere *</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-50" onClick={() => setNewCantiereDialog(true)} data-testid="inline-new-cantiere-btn">
                    <Plus className="h-3.5 w-3.5 mr-0.5" />Nuovo
                  </Button>
                </div>
                <Select value={createForm.cantiere_id} onValueChange={v => setCreateForm({...createForm, cantiere_id: v})}>
                  <SelectTrigger data-testid="create-cantiere-select"><SelectValue placeholder="Scegli cantiere..." /></SelectTrigger>
                  <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-base font-semibold">Operai Presenti *</Label>
              <div className="flex flex-wrap gap-2 min-h-[42px] p-2.5 border border-slate-200 rounded-md bg-white">
                {createForm.workers_list.map((w, idx) => (
                  <span key={w.worker_id || `manual-${w.name}-${idx}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-700">
                    {w.name}<button type="button" className="ml-0.5 text-blue-400 hover:text-red-500" onClick={() => removeCreateWorker(idx)}><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                {createForm.workers_list.length === 0 && <span className="text-sm text-slate-400">Seleziona operai...</span>}
              </div>
              <Select value="" onValueChange={addCreateWorker}>
                <SelectTrigger className="w-[220px]"><SelectValue placeholder="Aggiungi operaio..." /></SelectTrigger>
                <SelectContent>{workers.filter(w => !createForm.workers_list.some(wl => wl.worker_id === w.id)).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-1"><Label>Inizio</Label><input type="time" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={createForm.ora_inizio} onChange={e => { const v = e.target.value; const h = calcHours(v, createForm.ora_fine); setCreateForm({...createForm, ora_inizio: v, hours_worked: h, ore_straordinarie: h > 9 ? Math.round((h-9)*100)/100 : 0}); }} /></div>
              <div className="space-y-1"><Label>Fine</Label><input type="time" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={createForm.ora_fine} onChange={e => { const v = e.target.value; const h = calcHours(createForm.ora_inizio, v); setCreateForm({...createForm, ora_fine: v, hours_worked: h, ore_straordinarie: h > 9 ? Math.round((h-9)*100)/100 : 0}); }} /></div>
              <div className="space-y-1"><Label>Ore</Label><div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold">{formatHours(createForm.hours_worked, createForm.ora_inizio, createForm.ora_fine)}</div></div>
              <div className="space-y-1"><Label>Straordinari</Label><div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-amber-600">{formatHours(createForm.ore_straordinarie)}</div></div>
            </div>
            <div className="space-y-1"><Label>Descrizione</Label><Textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} rows={2} placeholder="Lavori eseguiti..." /></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Checkbox checked={createForm.trasferta} onCheckedChange={v => setCreateForm({...createForm, trasferta: v})} /><Label>Trasferta</Label></div>
              <div className="flex items-center gap-2">
                <Label>Stato:</Label>
                <Select value={createForm.status} onValueChange={v => setCreateForm({...createForm, status: v})}>
                  <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approvato">Approvato</SelectItem>
                    <SelectItem value="in_attesa">In Attesa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label>Acconto:</Label>
                <AccontoSelector compact value={createForm.acconto_riferimento} onChange={v => setCreateForm({...createForm, acconto_riferimento: v})} dataTestId="create-acconto-select" />
              </div>
            </div>
            <Button className="w-full bg-slate-900 text-white hover:bg-slate-800" onClick={handleCreateRapportino} data-testid="submit-create-rapportino">
              <Plus className="h-4 w-4 mr-2" />Crea Rapportino
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline New Cantiere Dialog */}
      <Dialog open={newCantiereDialog} onOpenChange={setNewCantiereDialog}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Nuovo cantiere</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateInlineCantiere} className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label>Nome cantiere *</Label>
              <Input value={newCantiereForm.name} onChange={e => setNewCantiereForm({...newCantiereForm, name: e.target.value})} autoFocus data-testid="new-cantiere-name" />
            </div>
            <div className="space-y-1">
              <Label>Indirizzo</Label>
              <Input value={newCantiereForm.address} onChange={e => setNewCantiereForm({...newCantiereForm, address: e.target.value})} data-testid="new-cantiere-address" />
            </div>
            <div className="space-y-1">
              <Label>Cliente (opzionale)</Label>
              <Input value={newCantiereForm.client_name} onChange={e => setNewCantiereForm({...newCantiereForm, client_name: e.target.value})} />
            </div>
            <p className="text-xs text-slate-400">Verrà selezionato automaticamente per il rapportino in corso.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setNewCantiereDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={creatingCantiere} className="bg-slate-900 text-white" data-testid="confirm-new-cantiere">
                {creatingCantiere ? 'Creazione...' : 'Crea e seleziona'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Minimized floating pill */}
      {createOpen && createMinimized && (
        <div
          className="fixed bottom-4 right-4 bg-slate-900 text-white rounded-full shadow-lg flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-slate-800 transition z-50"
          onClick={() => setCreateMinimized(false)}
          data-testid="minimized-rapportino-pill"
        >
          <Pencil className="h-4 w-4" />
          <span className="text-sm font-medium">Rapportino in corso</span>
          {createForm.cantiere_id && (
            <Badge variant="outline" className="bg-white/10 text-white border-white/20 ml-1">
              {getCantiereNome(createForm.cantiere_id) || '...'}
            </Badge>
          )}
          <button
            type="button"
            className="ml-2 text-white/60 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Annullare il rapportino in corso? I dati andranno persi.')) {
                setCreateOpen(false);
                setCreateMinimized(false);
                setCreateForm({ date: new Date().toISOString().split('T')[0], hours_worked: 8, ore_straordinarie: 0, ora_inizio: '08:00', ora_fine: '16:00', description: '', cantiere_id: '', trasferta: false, status: 'approvato', acconto_riferimento: '', workers_list: [] });
              }
            }}
            data-testid="discard-minimized-rapportino"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
